import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/global.css';
import './Nav.css';

const getSearchContext = (pathname) => {
  if (pathname.startsWith('/mydata')) return 'mydata';
  if (pathname.startsWith('/mynotes')) return 'mynotes';
  if (pathname.startsWith('/messages')) return 'messages';
  //if (pathname.startsWith('/contacts')) return 'contacts';
  return null;
};

function Nav({
  userId,
  productCode,
  userFirstName,
  userLastName,
  userEmail,
  searchTerm,
  setSearchTerm,
  setSearchType,
  searchPlaceholder,
  entryScope = 'all',
  setEntryScope,
  handleLogout,
  unreadMessageCount = 0,
}) {
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const accBtnRef = useRef(null);
  const accMenuRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const isNotesSite =
    window.location.hostname === 'mysimpledbnotes.com' ||
    window.location.hostname === 'www.mysimpledbnotes.com';

  const [menuOpen, setMenuOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(true);
  const [secOpen, setSecOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const [searchInput, setSearchInput] = useState(searchTerm || '');

  const isLoggedIn = useMemo(() => !!userId, [userId]);

  const hasNotes = productCode === 'notes' || productCode === 'mysimpledb';
  const hasMySimpleDB = productCode === 'mysimpledb';

  const normalizedUnreadCount = Math.max(
    0,
    Number.isFinite(Number(unreadMessageCount))
      ? Math.floor(Number(unreadMessageCount))
      : 0
  );
  const unreadCountLabel =
    normalizedUnreadCount > 99 ? '99+' : String(normalizedUnreadCount);

  const previousSearchContextRef = useRef(getSearchContext(location.pathname));

  useEffect(() => {
    const previousContext = previousSearchContextRef.current;
    const currentContext = getSearchContext(location.pathname);

    if (
      previousContext &&
      currentContext &&
      previousContext !== currentContext
    ) {
      setSearchType('');
      setSearchInput('');
      setSearchTerm('');
    }

    previousSearchContextRef.current = currentContext;
  }, [location.pathname, setSearchTerm, setSearchType]);

  const showSearch = useMemo(() => {
    if (!isLoggedIn) return false;

    const path = location.pathname;

    return path === '/mydata' || path === '/mynotes';
  }, [isLoggedIn, location.pathname]);

  const userLabel = useMemo(() => {
    if (!isLoggedIn) return '';
    const fn = (userFirstName || '').trim();
    const ln = (userLastName || '').trim();
    const li = ln ? `${ln[0].toUpperCase()}.` : '';
    if (fn && li) return `${fn} ${li}`;
    if (fn) return fn;
    if (ln) return ln;
    return 'Account';
  }, [isLoggedIn, userFirstName, userLastName]);

  useEffect(() => {
    setSearchInput(searchTerm || '');
  }, [searchTerm]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const t = setTimeout(() => {
      setSearchType('');
      setSearchTerm(searchInput);
    }, 300);

    return () => clearTimeout(t);
  }, [searchInput, isLoggedIn, setSearchTerm, setSearchType]);

  const onChange = (e) => {
    setSearchInput(e.target.value);
  };

  const clearSearch = () => {
    setSearchType('');
    setSearchInput('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea';

      if (!isTyping && e.key === '/' && isLoggedIn) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setAccountMenuOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isLoggedIn]);

  useEffect(() => {
    const onClick = (e) => {
      if (accountMenuOpen) {
        const clickedAccBtn = accBtnRef.current?.contains(e.target);
        const clickedAccMenu = accMenuRef.current?.contains(e.target);
        if (!clickedAccBtn && !clickedAccMenu) setAccountMenuOpen(false);
      }
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [accountMenuOpen]);

  const Toggle = ({ open, onClick, label }) => (
    <button
      className="menu-toggle"
      onClick={onClick}
      aria-expanded={open}
      aria-label={`Toggle ${label}`}
      type="button"
    >
      {open ? '–' : '+'}
    </button>
  );

  const closeDrawer = () => setMenuOpen(false);

  const goToProfile = () => {
    setAccountMenuOpen(false);
    navigate('/account/profile');
  };

  const signOut = async () => {
    await handleLogout?.();
    setAccountMenuOpen(false);
    setMenuOpen(false);
    navigate('/');
  };

  const drawerGo = (path) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <div className="navbar">
      <div className="nav-left">
        {isNotesSite ? (
          <>
            <Link to="/" className="logo" aria-label="MySimpleDBNotes Home">
              <img
                src="/images/mysimpledbnotes-logo.png"
                alt="MySimpleDBNotes Logo"
                className="logo-image"
              />
            </Link>
          </>
        ) : (
          <>
            <Link to="/" className="logo" aria-label="MySimpleDB Home">
              <img
                src="/images/mysimpledb-logo.png"
                alt="MySimpleDB Logo"
                className="logo-image"
              />
              {window.syncApi && (
                <span className="app-mode-badge">Desktop</span>
              )}
            </Link>
          </>
        )}
      </div>

      <div className="nav-controls">
        {showSearch && location.pathname.startsWith('/mydata') && (
          <div
            className="nav-entry-scope"
            role="group"
            aria-label="Entries to show"
          >
            <button
              type="button"
              className={`nav-entry-scope__button ${
                entryScope === 'all' ? 'is-active' : ''
              }`}
              aria-pressed={entryScope === 'all'}
              onClick={() => setEntryScope?.('all')}
            >
              All entries
            </button>
            <button
              type="button"
              className={`nav-entry-scope__button ${
                entryScope === 'mine' ? 'is-active' : ''
              }`}
              aria-pressed={entryScope === 'mine'}
              onClick={() => setEntryScope?.('mine')}
            >
              My entries
            </button>
          </div>
        )}

        {showSearch && (
          <div className="nav-searchbox" role="search">
            <svg
              className="nav-searchbox__icon"
              width="16"
              height="16"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M13.9 12.5l4 4a1 1 0 01-1.4 1.4l-4-4a7 7 0 111.4-1.4zM8.5 14a5.5 5.5 0 100-11 5.5 5.5 0 000 11z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              id="search"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={onChange}
              className="nav-searchbox__input"
              aria-label="Search"
            />
            {searchInput ? (
              <button
                type="button"
                className="nav-searchbox__clear"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            ) : null}
          </div>
        )}

        <div className="nav-icons">
          {isLoggedIn ? (
            <div className="nav-account">
              <button
                ref={accBtnRef}
                type="button"
                className="nav-account__btn"
                onClick={() => setAccountMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                aria-label="Open account menu"
              >
                <svg
                  className="nav-auth__icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1a1 1 0 001 1h14a1 1 0 001-1v-1c0-2.761-3.582-5-8-5z" />
                </svg>

                <span
                  className="nav-account__label"
                  title={userEmail || userLabel}
                >
                  {userLabel}
                </span>

                <span className="nav-account__caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              {accountMenuOpen && (
                <div
                  ref={accMenuRef}
                  className="nav-account__menu"
                  role="menu"
                  aria-label="Account menu"
                >
                  <div className="nav-account__meta">
                    <div className="nav-account__metaTitle">Signed in as</div>
                    <div className="nav-account__metaValue">
                      {userEmail || '—'}
                    </div>
                  </div>

                  <div className="nav-account__divider" />

                  <button
                    type="button"
                    className="nav-account__item"
                    role="menuitem"
                    onClick={goToProfile}
                  >
                    Account
                  </button>

                  <button
                    type="button"
                    className="nav-account__item nav-account__danger"
                    role="menuitem"
                    onClick={signOut}
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="nav-auth"
              onClick={() => navigate('/login')}
              aria-label="Sign in"
            >
              <svg
                className="nav-auth__icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 12c2.761 0 5-2.239 5-5S14.761 2 12 2 7 4.239 7 7s2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1a1 1 0 001 1h14a1 1 0 001-1v-1c0-2.761-3.582-5-8-5z" />
              </svg>
              <span className="nav-auth__label">SIGN IN</span>
            </button>
          )}

          <button
            type="button"
            className="icon-plain nav-menu-button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={
              normalizedUnreadCount > 0
                ? `Open menu, ${normalizedUnreadCount} unread message${
                    normalizedUnreadCount === 1 ? '' : 's'
                  }`
                : 'Open menu'
            }
            onClick={() => {
              setAccountMenuOpen(false);
              setMenuOpen((v) => !v);
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z"
              />
            </svg>
            {normalizedUnreadCount > 0 && (
              <span className="nav-menu-unread-badge" aria-hidden="true">
                {unreadCountLabel}
              </span>
            )}
          </button>
        </div>
      </div>

      <div
        className={`drawer-backdrop ${menuOpen ? 'is-open' : ''}`}
        onClick={closeDrawer}
      />

      <aside
        ref={menuRef}
        className={`drawer ${menuOpen ? 'is-open' : ''}`}
        role="menu"
        aria-label="Main Menu"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer-header">
          <span className="drawer-title">Menu</span>
          <button
            className="drawer-close"
            onClick={closeDrawer}
            aria-label="Close menu"
            type="button"
          >
            ×
          </button>
        </div>

        <nav className="drawer-nav">
          <ul className="menu-root">
            {isLoggedIn ? (
              <>
                {hasMySimpleDB && !isNotesSite && (
                  <li>
                    <button
                      type="button"
                      className="menu-item btnlike"
                      onClick={drawerGo('/mydata')}
                    >
                      MyData
                    </button>
                  </li>
                )}

                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/mynotes')}
                  >
                    MyNotes
                  </button>
                </li>

                {hasMySimpleDB && !isNotesSite && (
                  <li>
                    <button
                      type="button"
                      className="menu-item btnlike menu-item--with-badge"
                      onClick={drawerGo('/messages')}
                      aria-label={
                        normalizedUnreadCount > 0
                          ? `MyMessages, ${normalizedUnreadCount} unread message${
                              normalizedUnreadCount === 1 ? '' : 's'
                            }`
                          : 'MyMessages'
                      }
                    >
                      <span>MyMessages</span>
                      {normalizedUnreadCount > 0 && (
                        <span className="menu-unread-pill" aria-hidden="true">
                          {normalizedUnreadCount === 1
                            ? 'NEW'
                            : `${unreadCountLabel} NEW`}
                        </span>
                      )}
                    </button>
                  </li>
                )}

                <li className="menu-section">
                  <div className="menu-row">
                    <div className="menu-section__title">Account</div>
                    <Toggle
                      open={accOpen}
                      onClick={() => setAccOpen((o) => !o)}
                      label="Account"
                    />
                  </div>

                  {accOpen && (
                    <ul className="menu-sub">
                      <li>
                        <button
                          type="button"
                          className="menu-item btnlike"
                          onClick={drawerGo('/account/profile')}
                        >
                          Profile
                        </button>
                      </li>
                      {hasMySimpleDB && !isNotesSite && (
                        <>
                          <li>
                            <button
                              type="button"
                              className="menu-item btnlike"
                              onClick={drawerGo('/account/public-profile')}
                            >
                              Public Profile
                            </button>
                          </li>

                          <li>
                            <button
                              type="button"
                              className="menu-item btnlike"
                              onClick={drawerGo('/account/usage')}
                            >
                              Usage
                            </button>
                          </li>

                          <li>
                            <button
                              type="button"
                              className="menu-item btnlike"
                              onClick={drawerGo('/account/people-sharing')}
                            >
                              Sharing &amp; Visibility
                            </button>
                          </li>
                        </>
                      )}

                      <li className="menu-subsection">
                        <div className="menu-row">
                          <div className="menu-subsection__title">Security</div>
                          <Toggle
                            open={secOpen}
                            onClick={() => setSecOpen((o) => !o)}
                            label="Security"
                          />
                        </div>

                        {secOpen && (
                          <ul className="menu-sub">
                            <li>
                              <button
                                type="button"
                                className="menu-item btnlike"
                                onClick={drawerGo('/account/security/overview')}
                              >
                                Overview
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="menu-item btnlike"
                                onClick={drawerGo('/account/security/password')}
                              >
                                Password
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="menu-item btnlike"
                                onClick={drawerGo('/account/security/twofa')}
                              >
                                Two-Factor Authentication
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="menu-item btnlike"
                                onClick={drawerGo('/account/security/recovery')}
                              >
                                Recovery Codes
                              </button>
                            </li>
                            {hasMySimpleDB && !isNotesSite && (
                              <li>
                                <button
                                  type="button"
                                  className="menu-item btnlike"
                                  onClick={drawerGo('/account/security/vault')}
                                >
                                  Sensitive Vault
                                </button>
                              </li>
                            )}
                          </ul>
                        )}
                      </li>

                      {hasMySimpleDB && !isNotesSite && (
                        <li>
                          <button
                            type="button"
                            className="menu-item btnlike"
                            onClick={drawerGo('/account/settings')}
                          >
                            Site Settings
                          </button>
                        </li>
                      )}

                      <li>
                        <button
                          type="button"
                          className="menu-item btnlike"
                          onClick={drawerGo('/account/billing')}
                        >
                          Subscription
                        </button>
                      </li>
                    </ul>
                  )}
                </li>

                <li className="menu-section">
                  <div className="menu-row">
                    <div className="menu-section__title">Legal</div>
                    <Toggle
                      open={legalOpen}
                      onClick={() => setLegalOpen((o) => !o)}
                      label="Legal"
                    />
                  </div>

                  {legalOpen && (
                    <ul className="menu-sub">
                      <li>
                        <button
                          type="button"
                          className="menu-item btnlike"
                          onClick={drawerGo('/privacy')}
                        >
                          Privacy
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="menu-item btnlike"
                          onClick={drawerGo('/terms')}
                        >
                          Terms
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className="menu-item btnlike"
                          onClick={drawerGo('/sms-terms')}
                        >
                          SMS Terms
                        </button>
                      </li>
                    </ul>
                  )}
                </li>

                <li className="menu-divider" />

                <li>
                  <button
                    className="menu-item btnlike"
                    onClick={signOut}
                    type="button"
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/')}
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/create-account')}
                  >
                    Try Free
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/login')}
                  >
                    Log In
                  </button>
                </li>

                <li className="menu-divider" />

                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/privacy')}
                  >
                    Privacy
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/terms')}
                  >
                    Terms
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/sms-terms')}
                  >
                    SMS Terms
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="menu-item btnlike"
                    onClick={drawerGo('/sms-signup')}
                  >
                    SMS Notifications
                  </button>
                </li>
              </>
            )}
          </ul>
        </nav>
      </aside>
    </div>
  );
}

export default Nav;
